import React, { useMemo, useState, useEffect } from 'react';
import {
  MaterialReactTable,
  MRT_PaginationState,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { Box, IconButton, Tooltip } from '@mui/material';
import { ArticleOutlined, DownloadOutlined } from '@mui/icons-material';
import { DailyReport } from '../../Types/DailyReport';
import { Timestamp } from 'firebase/firestore';
import './DailyReportsTable.css';

type TableDailyReport = Omit<DailyReport, 'generated_at' | 'payed_at' | 'period' | 'status'> & {
  generated_at: string;
  payed_at?: string;
  period: {
    start: string;
    end: string;
  };
  rowNumber: number;
};

const formatDate = (date: { seconds: number, nanoseconds: number } | undefined): string => {
  if (!date) return '';
  
  try {
    const timestamp = new Timestamp(date.seconds, date.nanoseconds);
    const dateObject = timestamp.toDate();
    
    const formattedDate = dateObject.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    
    const formattedTime = dateObject.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    return `${formattedDate} ${formattedTime}`;
  } catch (error) {
    console.error('Error formatting date:', error, date);
    return 'Fecha inválida';
  }
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'BOB',
    minimumFractionDigits: 2
  }).format(amount);
};

const getTableDailyReport = (report: DailyReport, index: number): TableDailyReport => {
  return {
    ...report,
    generated_at: formatDate(report.generated_at),
    payed_at: report.payed_at ? formatDate(report.payed_at) : undefined,
    period: {
      start: formatDate(report.period.start),
      end: formatDate(report.period.end)
    },
    rowNumber: index + 1
  };
};

const downloadFile = (url: string, filename: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

interface DailyReportsTableProps {
  isLoading: boolean;
  initialPageSize: number;
  initialPageIndex: number;
  onPageSizeChange: (newSize: number) => void;
  onPageIndexChange: (newIndex: number) => void;
  dailyReports: DailyReport[];
  totalRowCount: number;
}

const DailyReportsTable: React.FC<DailyReportsTableProps> = ({ 
  isLoading,
  initialPageSize,
  initialPageIndex,
  onPageSizeChange,
  onPageIndexChange,
  dailyReports,
  totalRowCount
}) => {

  const tableDailyReports = useMemo(() => {
    return dailyReports.map((report: DailyReport, index: number) => getTableDailyReport(report, index));
  }, [dailyReports]);

  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: initialPageSize
  });

  useEffect(() => {
    if (pagination.pageSize !== initialPageSize) {
      onPageSizeChange(pagination.pageSize);
    }
    if (pagination.pageIndex !== initialPageIndex) {
      onPageIndexChange(pagination.pageIndex);
    }
  }, [pagination.pageSize, pagination.pageIndex, initialPageSize, initialPageIndex, onPageSizeChange, onPageIndexChange]);

  const columns = useMemo<MRT_ColumnDef<TableDailyReport>[]>(() => [
    {
      accessorKey: 'rowNumber',
      header: 'No.',
      size: 60,
      enableSorting: false,
    },
    {
      accessorKey: 'id',
      header: 'Identificador',
      size: 120,
    },
    {
      accessorKey: 'generated_at',
      header: 'Generado',
      size: 150,
    },
    {
      accessorKey: 'payed_at',
      header: 'Fecha pago',
      size: 150,
      Cell: ({ cell }: { cell: any }) => {
        const value = cell.getValue();
        return value || '-';
      }
    },
    {
      accessorKey: 'amount',
      header: 'Monto',
      size: 120,
      Cell: ({ cell }: { cell: any }) => {
        return formatCurrency(cell.getValue());
      }
    },
    {
      accessorKey: 'commission',
      header: 'Comisión',
      size: 120,
      Cell: ({ cell }: { cell: any }) => {
        return formatCurrency(cell.getValue());
      }
    },
    {
      accessorKey: 'total_amount',
      header: 'Total',
      size: 120,
      Cell: ({ cell }: { cell: any }) => {
        return formatCurrency(cell.getValue());
      }
    },
    {
      id: 'actions',
      header: 'Acciones',
      size: 120,
      enableSorting: false,
      Cell: ({ row }: { row: any }) => {
        const report = row.original;
        return (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Ver Reporte PDF">
              <IconButton
                size="small"
                onClick={() => downloadFile(report.pdf_url, `reporte-${report.id}.pdf`)}
                sx={{ color: 'primary.main' }}
              >
                <DownloadOutlined />
              </IconButton>
            </Tooltip>
            {report.payment_receipt_url && (
              <Tooltip title="Ver Recibo">
                <IconButton
                  size="small"
                  onClick={() => downloadFile(report.payment_receipt_url, `recibo-${report.id}.jpg`)}
                  sx={{ color: 'success.main' }}
                >
                  <ArticleOutlined />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      }
    },
  ], []);

  const table = useMaterialReactTable({
    data: tableDailyReports,
    columns,
    state: {
      pagination,
      isLoading,
    },
    onPaginationChange: setPagination,
    enablePagination: true,
    manualPagination: true,
    rowCount: totalRowCount,
    localization: {
      rowsPerPage: 'Filas por página',
    },
  });

  return (
    <div className="daily-reports-table-container">
      <MaterialReactTable table={table} />
    </div>
  );
};

export default DailyReportsTable; 